import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Copy, KeyRound, Loader2, PlugZap, XCircle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Card, fmtDate } from "./shared";

const inputCls = "h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm outline-none focus:border-[var(--saffron)]";

export const MatchSettings = () => {
  const { data, refetch } = useQuery({ queryKey: ["match-settings"], queryFn: () => api.get("/staff/matchmaking/settings").then((r) => r.data) });
  const [url, setUrl] = useState("");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState("");
  const [test, setTest] = useState(null);
  useEffect(() => data && setUrl(data.api_url || ""), [data]);
  const callbackUrl = `${window.location.origin}/api/public/match/callback`;

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      await api.put("/staff/matchmaking/settings", { api_url: url, api_key: key });
      setKey("");
      toast.success("MatchMaking settings saved securely");
      refetch();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy("");
  };

  const runTest = async () => {
    setBusy("test");
    setTest(null);
    try {
      setTest((await api.post("/staff/matchmaking/test")).data);
    } catch (err) {
      setTest({ ok: false, message: formatApiError(err) });
    }
    setBusy("");
  };

  return (
    <Card data-testid="match-settings-card">
      <p className="flex items-center gap-2 font-semibold"><KeyRound className="h-4 w-4 text-[var(--saffron-deep)]" /> MatchMaking API</p>
      <p className="mt-1 text-sm text-neutral-500">The key is encrypted on the server and never shown again — not to staff, not in the browser.</p>
      <form onSubmit={save} className="mt-4 space-y-4" data-testid="match-settings-form">
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="mm-url">API address</label>
          <input id="mm-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/api/match" className={inputCls} data-testid="match-settings-url-input" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="mm-key">Site API key (X-Match-Key)</label>
          <input id="mm-key" type="password" autoComplete="new-password" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder={data?.key_set ? `Saved ${data.key_hint} — leave blank to keep it` : "Paste the key from Project Activation → MatchMaking API"}
            className={inputCls} data-testid="match-settings-key-input" />
          <p className="mt-1 text-xs text-neutral-500" data-testid="match-settings-key-status">
            {data?.key_set ? `Key saved ${data.key_hint}${data.updated_at ? ` · updated ${fmtDate(data.updated_at)} by ${data.updated_by}` : ""}` : "No key saved yet — visitors get the personal shortlist flow until you add one."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button disabled={busy === "save"} className="btn-saffron h-11 rounded-full px-6 text-sm font-semibold" data-testid="match-settings-save-button">
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save securely"}
          </button>
          <button type="button" onClick={runTest} disabled={!data?.key_set || !!busy} className="flex h-11 items-center gap-2 rounded-full border border-neutral-300 px-5 text-sm font-semibold disabled:opacity-40" data-testid="match-settings-test-button">
            {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test connection
          </button>
        </div>
        {test && (
          <p className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${test.ok ? "bg-[#138808]/10 text-[#0f6d06]" : "bg-red-50 text-red-700"}`} data-testid="match-settings-test-result">
            {test.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {test.message}{test.summary && Object.keys(test.summary).length ? ` · ${Object.entries(test.summary).map(([t, n]) => `~${t}%: ${n}`).join(", ")}` : ""}
          </p>
        )}
      </form>
      <div className="mt-5 rounded-xl bg-[#fbf8f3] p-3 text-sm">
        <p className="font-medium">Callback URL (paste into CPN for this site)</p>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 break-all text-xs text-neutral-700" data-testid="match-settings-callback-url">{callbackUrl}</code>
          <button type="button" onClick={() => navigator.clipboard.writeText(callbackUrl).then(() => toast.success("Copied"))} className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-300" aria-label="Copy callback URL" data-testid="match-settings-callback-copy">
            <Copy className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Card>
  );
};
